import { Blurb, Row, Write, Zone } from '@gimlite/ui-v1';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { EventEntity, EventEnum } from '../../client/graphql';
import { SessionAction } from '../action/session.action';
import { SessionInfos } from '../infos/session/session.infos';
import { useTranslation } from '@gimlite/common';

class Static {
  static prioritySelectEntry: Array<
    Extract<EventEntity['state'], 'CREATED' | 'ENTERED' | 'ENTRY_DENIED'>
  > = [EventEnum.Entered, EventEnum.EntryDenied, EventEnum.Created];

  static prioritySelectExit: Array<
    Extract<
      EventEntity['state'],
      'EXIT_AUTHORIZE_PENDING' | 'EXIT_AUTHORIZED' | 'EXIT_DENIED' | 'EXITED'
    >
  > = [
    EventEnum.Exited,
    EventEnum.ExitDenied,
    EventEnum.ExitAuthorized,
    EventEnum.ExitAuthorizePending,
  ];
}

export declare namespace SessionLineType {
  type Props = {
    data: {
      firstName: string | null;
      lastName: string | null;
      entryDate: string;
      exitDate?: string;
      sessionId: string;
      productName: string | null;
      contractCategory?: string | null;
      motoristId: string;
      contractId: string;
      parkingId: string;
      isInconsistent: boolean;
      events: Array<
        EventEntity & {
          accessPointCode?: string | null;
          currency?: string | null;
          createdAt: string;
        }
      >;
    };
  };
}

export const SessionLine = ({ data }: SessionLineType.Props) => {
  const { t } = useTranslation();

  const {
    firstName,
    lastName,
    productName,
    entryDate,
    exitDate,
    sessionId,
    motoristId,
    contractId,
    parkingId,
    isInconsistent,
    events,
    contractCategory,
  } = data;

  const eventsFormatted = useMemo(
    () =>
      events.reduce(
        (acc: Record<string, EventEntity | null>, { state, ...rest }) =>
          state ? { ...acc, [state]: { state, ...rest } } : acc,
        {},
      ),
    [data],
  );

  const selectedEntryState = Static.prioritySelectEntry.find(
    (state) => eventsFormatted[state],
  );

  const selectedEntry = selectedEntryState
    ? eventsFormatted[selectedEntryState]
    : eventsFormatted[
        Static.prioritySelectEntry[Static.prioritySelectEntry.length - 1]
      ];

  const selectedExitState = Static.prioritySelectExit.find(
    (state) => eventsFormatted[state],
  );

  const selectedExit = selectedExitState
    ? eventsFormatted[selectedExitState]
    : eventsFormatted[
        Static.prioritySelectExit[Static.prioritySelectExit.length - 1]
      ];

  const formattedDate = useCallback(
    (date?: string) => {
      return new Date(date || 0).getTime() === 0
        ? '-'
        : DateTime.fromISO(date || '').toFormat('dd/MM/yyyy HH:mm:ss');
    },
    [selectedEntry, selectedExit],
  );

  return (
    <Zone
      config={{
        gap: {
          x: 1,
          y: 0,
        },
        zones: [['identity', 'entryDate', 'exitDate', 'showSession', 'action']],
        rows: ['1fr'],
        columns: ['1fr', '1fr', '1fr', 'min-content', 'min-content'],
        vertical: 'center',
        horizontal: 'center',
      }}
    >
      <Zone.Area config={{ area: 'identity' }}>
        <Blurb
          data={{
            title:
              `${firstName || ''} ${lastName || ''}`.trim() || t('unknown'),
            description: productName ?? undefined,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'entryDate' }}>
        <Row config={{ horizontal: 'start', vertical: 'center' }}>
          <Write
            data={{
              item: formattedDate(entryDate),
            }}
            config={{
              mode: 'key-small-bold',
              color:
                isInconsistent ||
                selectedEntry?.state.includes('DENIED') ||
                events.find(({ state }) => state.includes('DENIED'))
                  ? 'error'
                  : undefined,
            }}
          ></Write>
        </Row>
      </Zone.Area>

      <Zone.Area config={{ area: 'exitDate' }}>
        <Row config={{ horizontal: 'start', vertical: 'center' }}>
          {exitDate && (
            <Write
              data={{
                item: formattedDate(exitDate),
              }}
              config={{
                mode: 'key-small-bold',
                color:
                  isInconsistent ||
                  selectedExit?.state.includes('DENIED') ||
                  events.find(({ state }) => state.includes('DENIED'))
                    ? 'error'
                    : undefined,
              }}
            ></Write>
          )}
        </Row>
      </Zone.Area>

      <Zone.Area config={{ area: 'showSession' }}>
        <Row config={{ horizontal: 'center', vertical: 'center' }}>
          <SessionInfos data={{ sessionId: sessionId, events: events }} />
        </Row>
      </Zone.Area>

      <Zone.Area config={{ area: 'action' }}>
        <Row config={{ horizontal: 'center', vertical: 'center' }}>
          <SessionAction
            config={{ size: 'list' }}
            data={{
              sessionId,
              motoristId,
              contractId,
              parkingId,
              contractCategory,
            }}
          />
        </Row>
      </Zone.Area>
    </Zone>
  );
};
